import { useState } from 'react';
import { useT } from '@/store';
import { Drawer } from '@/components/ui/Overlay';
import { OrderFormFields } from './OrderForm';
import { useOrderForm } from '@/hooks/useOrderForm';

/**
 * The Orders tab's create form. Same fields and validation as the
 * dispatcher's QuickDispatchModal (both call useOrderForm), but it stays
 * open after a successful create and shows a confirmation — entering the
 * morning's jobs is a batch task, and closing the panel after each one
 * would mean reopening it six times.
 */
export function CreateOrderDrawer({ open, onClose }) {
  const t = useT();
  const [createdLabel, setCreatedLabel] = useState('');
  const [createdCount, setCreatedCount] = useState(0);
  const form = useOrderForm({
    // Kiek reisu is tikruju gimė. Maršrutas grąžina created_order_ids —
    // vienas elementas paprastam užsakymui, penki rezervavimui su penkiais
    // reisais. Be šito dispečeris, paspaudęs "5 reisai", pamatytų tą patį
    // "Sukurta ir išsiųsta ✓", kaip ir paspaudęs vieną, ir neturėtų jokio
    // patvirtinimo, kad skaičius apskritai buvo išgirstas.
    onCreated: (order, meta) => {
      setCreatedLabel(order?.waybill_number || order?.order_id || '');
      setCreatedCount(meta?.created_order_ids?.length ?? 1);
    },
  });

  // V-289. This component never unmounts between opens (Drawer only hides
  // its own content when closed, per Overlay.jsx) — every dismissal must
  // clear the form too, not just createdLabel, or the NEXT open reappears
  // pre-filled with a previous, unrelated order's values and error.
  const close = () => {
    setCreatedLabel('');
    setCreatedCount(0);
    form.reset();
    onClose?.();
  };

  return (
    <Drawer
      open={open}
      onClose={close}
      title={t('orders.newOrder')}
      footer={
        <>
          <button type="button" className="btn" onClick={close}>
            {t('common.closeBtn')}
          </button>
          <button type="button" className="btn btn-primary" onClick={form.submit} disabled={form.isPending}>
            {t(form.isPending ? 'common.loading' : 'orders.createBtn')}
          </button>
        </>
      }
    >
      <OrderFormFields values={form.values} set={form.set} setValue={form.setValue} />

      {form.errorText ? (
        <p className="mt-3 text-[11.5px] text-red" role="alert">
          {form.errorText}
        </p>
      ) : null}

      {createdLabel && !form.errorText ? (
        <p className="mt-3 text-[11.5px] text-green" role="status">
          {createdCount > 1
            ? t('orders.createdLoads', { loads: createdCount })
            : t('orders.createdAndDispatched')}{' · '}{createdLabel}
        </p>
      ) : null}
    </Drawer>
  );
}
